import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'lucide-react';
import { formatIqd, fromMillidinars, type DeviceDto } from '@gc/shared';

import { menuItemsFor, type DeviceMenuItem, type DeviceMenuItemId } from './deviceMenu';

/**
 * The per-device Actions menu (wave 3 #5), hand-rolled — no dropdown component
 * exists anywhere in this app, and pulling a library in for one popover is not
 * this wave's decision to make.
 *
 * Behaviour contract:
 * - Escape closes and returns focus to the trigger;
 * - clicking outside closes (focus stays where the user clicked — they left
 *   the menu on purpose);
 * - ArrowUp/ArrowDown move between items, wrapping;
 * - choosing an item closes, returns focus to the trigger, then acts.
 *
 * WHAT the menu offers is `menuItemsFor` (`deviceMenu.ts`), computed from the
 * same permission keys the API gates with; this file only draws that list.
 */

export function DeviceActionsMenu({
  device,
  currencySymbol,
  can,
  onSelect,
}: {
  device: DeviceDto;
  currencySymbol: string;
  /** Injected (not read from context) so the permission matrix is testable. */
  can: (permission: string) => boolean;
  onSelect: (device: DeviceDto, item: DeviceMenuItemId) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const items = menuItemsFor(device, can);

  // Outside click / tap closes. `pointerdown` rather than `click` so the menu
  // is gone before whatever was under the pointer receives its own event.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Focus the first actionable item on open, so keyboard users land inside.
  useEffect(() => {
    if (!open) return;
    const first = panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]');
    first?.focus();
  }, [open]);

  function close(options: { returnFocus: boolean }): void {
    setOpen(false);
    if (options.returnFocus) triggerRef.current?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close({ returnFocus: true });
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const buttons = Array.from(
        panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
      );
      if (buttons.length === 0) return;
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const next = buttons[(index + delta + buttons.length) % buttons.length];
      next?.focus();
    }
    // Tab is left alone: it naturally leaves the popover, and the blur below
    // is not needed — the pointerdown listener covers the mouse, and a menu
    // left open behind a Tab press closes on the next Escape or click.
  }

  const label = t('common.actions');

  return (
    <div ref={rootRef} className="relative" onKeyDown={handleKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        title={label}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        className="grid h-7 w-7 place-items-center rounded-full text-content-secondary transition-colors hover:bg-hover hover:text-content max-md:h-10 max-md:w-10"
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>

      {open ? (
        <div ref={panelRef}>
          <ActionsMenuPanel
            device={device}
            items={items}
            currencySymbol={currencySymbol}
            onSelect={(item) => {
              close({ returnFocus: true });
              onSelect(device, item);
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * The open popover, presentational and stateless — exported so the item list
 * (and the current-cost row) can be pinned by static-markup tests.
 */
export function ActionsMenuPanel({
  device,
  items,
  currencySymbol,
  onSelect,
}: {
  device: DeviceDto;
  items: readonly DeviceMenuItem[];
  currencySymbol: string;
  onSelect: (item: DeviceMenuItemId) => void;
}) {
  const { t } = useTranslation();
  const session = device.currentSession;

  return (
    <div
      role="menu"
      aria-label={t('deviceBoard.menu.title', { device: device.name })}
      className="absolute top-full z-30 mt-1 w-52 overflow-hidden rounded-card border border-subtle bg-raised py-1 shadow-2xl"
      style={{ insetInlineEnd: 0 }}
    >
      {items.length === 0 ? (
        <p className="px-3 py-2 text-xs text-content-muted">{t('deviceBoard.menu.empty')}</p>
      ) : null}

      {items.map((item) =>
        item.id === 'currentCost' && session ? (
          // A reading, not an action: the figure staff open this menu for.
          <div
            key={item.id}
            data-menu-item="currentCost"
            className="flex items-center justify-between gap-2 border-b border-subtle px-3 py-2 text-xs"
          >
            <span className="text-content-secondary">{t(item.labelKey)}</span>
            <span className="tabular font-semibold text-content">
              {formatIqd(fromMillidinars(session.runningCostMd), { currencySymbol })}
            </span>
          </div>
        ) : (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            data-menu-item={item.id}
            onClick={() => onSelect(item.id)}
            className={[
              'block w-full px-3 py-2 text-start text-sm transition-colors hover:bg-hover focus:bg-hover focus:outline-none',
              item.danger ? 'text-danger' : 'text-content',
            ].join(' ')}
          >
            {t(item.labelKey)}
          </button>
        ),
      )}
    </div>
  );
}
